import React from "react";
import { FieldArrayWithId, UseFieldArrayReplace } from "react-hook-form";

// Redux

// Services
import { getTranslateByKey } from "@/main/services/utils";
import { inRange } from "@/main/services/utilsNumber";
import { VariableTypeEnum } from "@/main/services/enum/VariableTypeEnum";

// Types
import { DynamicReportForm } from "../../../registerDynamicReport";

// Api

// Components
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import { IconButton, TableCell, TableRow } from "@mui/material";

import * as S from "./styles";

interface IVariableFieldProps {
	fields: FieldArrayWithId<DynamicReportForm, "variables", "id">[];
	startEdit: (variable: FieldArrayWithId<DynamicReportForm, "variables", "id">) => void;
	remove: (variable: FieldArrayWithId<DynamicReportForm, "variables", "id">) => void;
	replace: UseFieldArrayReplace<DynamicReportForm, "variables">;
}

function VariableList({ fields, replace, startEdit, remove }: IVariableFieldProps) {
	const sizeCollumns = [
		"45px", // ordering,
		"25%", // variableName,
		"15%", // variableType,
		"25%", // filterName,
		"15%", // maxAllowedRange,
		"10%", // mandatory,
		"80px", // Actions
	];

	const rowHeaders = [
		getTranslateByKey("dynamicReportVariable.ordering"),
		getTranslateByKey("dynamicReportVariable.variableName"),
		getTranslateByKey("dynamicReportVariable.variableType"),
		getTranslateByKey("dynamicReportVariable.filterName"),
		getTranslateByKey("dynamicReportVariable.maxAllowedRangeCol"),
		getTranslateByKey("dynamicReportVariable.mandatory"),
		"", // Actions
	];

	const fieldsOrdered = fields.sort((a, b) => a.ordering - b.ordering);
	const currentIndex = React.useRef<number | undefined>(undefined);

	function dragStart(ev: React.DragEvent<HTMLElement>) {
		const el = ev.currentTarget as HTMLElement;
		el.style.opacity = "0.1";
		currentIndex.current = Number(el.getAttribute("data-index"));
		const id = (ev.target as HTMLDivElement).id;
		ev.dataTransfer.setData("text/html", id);
		ev.dataTransfer.dropEffect = "move";
	}

	function dragEnd(ev: React.DragEvent<HTMLElement>) {
		const el = ev.target as HTMLElement;
		el.style.opacity = "1";
	}

	function dragEnter(ev: React.DragEvent<HTMLElement>) {
		const element = ev.target as HTMLElement;
		const el = ev.currentTarget as HTMLElement;
		const index = Number(el.getAttribute("data-index"));
		if (currentIndex.current !== undefined && currentIndex.current < index) {
			element.classList.add("after-listItemDragAndDrop");
		} else if (currentIndex.current !== undefined && currentIndex.current > index) {
			element.classList.add("before-listItemDragAndDrop");
		}
	}

	function dragOver(ev: React.DragEvent<HTMLElement>) {
		ev.preventDefault();
		ev.dataTransfer.dropEffect = "move";
	}

	function dragLeave(ev: React.DragEvent<HTMLElement>) {
		const element = ev.target as HTMLElement;
		const el = ev.currentTarget as HTMLElement;
		const index = Number(el.getAttribute("data-index"));
		if (currentIndex.current !== undefined && currentIndex.current <= index) {
			element.classList.remove("after-listItemDragAndDrop");
		} else if (currentIndex.current !== undefined && currentIndex.current > index) {
			element.classList.remove("before-listItemDragAndDrop");
		}
	}

	function drop(ev: React.DragEvent<HTMLElement>) {
		ev.preventDefault();
		const element = ev.target as HTMLElement;
		const el = ev.currentTarget as HTMLElement;
		const newIndex = Number(el.getAttribute("data-index"));
		let offset = 1;

		element.classList.remove("before-listItemDragAndDrop", "after-listItemDragAndDrop");
		if (currentIndex.current !== undefined) {
			const oldIndex = currentIndex.current;
			if (isNaN(newIndex)) {
				return;
			}

			if (newIndex == oldIndex) {
				offset = 0;
			} else if (newIndex > oldIndex) {
				offset = -1;
			}

			fields.forEach((item) => {
				if (item.ordering == oldIndex) {
					item.ordering = newIndex;
				} else if (inRange(item.ordering, newIndex, oldIndex)) {
					item.ordering = item.ordering + offset;
				}
			});
			replace(fields);
		}
	}

	function isDateRange(variable: FieldArrayWithId<DynamicReportForm, "variables", "id">): boolean {
		return !!variable.variableType && variable.variableType.key == VariableTypeEnum.DateRange;
	}

	function getVariableNames(variable: FieldArrayWithId<DynamicReportForm, "variables", "id">) {
		let variableText = variable.variableName;
		if (isDateRange(variable)) {
			variableText += ` / ${variable.variableRangeName}`;
		}
		return variableText;
	}

	return (
		<S.VariableGridArea>
			<BoxGrid rowHeaders={rowHeaders} sizeColumns={sizeCollumns} widthCustom="100%">
				{fieldsOrdered.map((variable) => {
					return (
						<TableRow
							data-index={variable.ordering}
							id={`id-dynamicReport-VariableList-${variable.id}`}
							key={variable.id}
							draggable
							onDragStart={dragStart}
							onDragEnd={dragEnd}
							onDragEnter={dragEnter}
							onDragOver={dragOver}
							onDragLeave={dragLeave}
							onDrop={drop}
						>
							<TableCell>{variable.ordering}</TableCell>
							<TableCell>{getVariableNames(variable)}</TableCell>
							<TableCell>{variable.variableType?.value}</TableCell>
							<TableCell>{variable.filterName}</TableCell>
							<TableCell>{isDateRange(variable) ? variable.maxAllowedRange : null}</TableCell>
							<TableCell>
								{variable.mandatory ? getTranslateByKey("information.yes") : getTranslateByKey("information.no")}
							</TableCell>

							<TableCell>
								<S.AreaActions>
									<IconButton
										key={variable.id + "edit"}
										id={`id-dynamicReport-VariableList-edit-${variable.id}`}
										onClick={() => startEdit(variable)}
									>
										<EditIcon />
									</IconButton>
									<IconButton
										key={variable.id + "remove"}
										id={`id-dynamicReport-VariableList-remove-${variable.id}`}
										onClick={() => remove(variable)}
									>
										<CloseIcon />
									</IconButton>
								</S.AreaActions>
							</TableCell>
						</TableRow>
					);
				})}
			</BoxGrid>
		</S.VariableGridArea>
	);
}

export default VariableList;
